import React, { useState, useEffect } from 'react'
import { Filter } from 'lucide-react'
import ProductCard from '../components/ProductCard'
import { productsAPI, categoriesAPI } from '../services/api'

const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [priceMin, setPriceMin] = useState(0)
  const [priceMax, setPriceMax] = useState(2000)
  const [sliderMin, setSliderMin] = useState(0)
  const [sliderMax, setSliderMax] = useState(2000)

  useEffect(() => {
    Promise.all([
      productsAPI.getAll(),
      categoriesAPI.getAll()
    ])
      .then(([productsRes, categoriesRes]) => {
        setProducts(productsRes.data)
        setCategories(categoriesRes.data)
        
        // Set price range based on products
        if (productsRes.data.length > 0) {
          const prices = productsRes.data.map(p => p.price)
          const min = Math.min(...prices)
          const max = Math.max(...prices)
          setSliderMin(min)
          setSliderMax(max)
          setPriceMin(min)
          setPriceMax(max)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filteredProducts = products.filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory
    const priceMatch = product.price >= priceMin && product.price <= priceMax
    return categoryMatch && priceMatch
  })

  const handleResetPrice = () => {
    setPriceMin(sliderMin)
    setPriceMax(sliderMax)
  }

  return (
    <div className="min-h-screen bg-gray-50" data-component-path="client/src/pages/Shop.jsx" data-component-line="128:4" data-component-id="client/src/pages/Shop.jsx:128:4">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-component-path="client/src/pages/Shop.jsx" data-component-line="130:6" data-component-id="client/src/pages/Shop.jsx:130:6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-component-path="client/src/pages/Shop.jsx" data-component-line="131:8" data-component-id="client/src/pages/Shop.jsx:131:8">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-800 mb-4" data-component-path="client/src/pages/Shop.jsx" data-component-line="132:10" data-component-id="client/src/pages/Shop.jsx:132:10">
            Our Collection
          </h1>
          <p className="text-gray-600 text-lg" data-component-path="client/src/pages/Shop.jsx" data-component-line="135:10" data-component-id="client/src/pages/Shop.jsx:135:10">
            Explore our exquisite range of handcrafted earrings
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-component-path="client/src/pages/Shop.jsx" data-component-line="141:6" data-component-id="client/src/pages/Shop.jsx:141:6">
        <div className="flex flex-col lg:flex-row gap-8" data-component-path="client/src/pages/Shop.jsx" data-component-line="142:8" data-component-id="client/src/pages/Shop.jsx:142:8">
          {/* Filters Sidebar */}
          <div className="lg:w-64 flex-shrink-0" data-component-path="client/src/pages/Shop.jsx" data-component-line="144:10" data-component-id="client/src/pages/Shop.jsx:144:10">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24" data-component-path="client/src/pages/Shop.jsx" data-component-line="145:12" data-component-id="client/src/pages/Shop.jsx:145:12">
              <div className="flex items-center gap-2 mb-6" data-component-path="client/src/pages/Shop.jsx" data-component-line="146:14" data-component-id="client/src/pages/Shop.jsx:146:14">
                <Filter className="w-5 h-5 text-champagne-600" data-component-path="client/src/pages/Shop.jsx" data-component-line="147:16" data-component-id="client/src/pages/Shop.jsx:147:16" />
                <h2 className="font-serif text-xl font-semibold text-gray-800" data-component-path="client/src/pages/Shop.jsx" data-component-line="148:16" data-component-id="client/src/pages/Shop.jsx:148:16">Filters</h2>
              </div>

              {/* Category Filter */}
              <div className="mb-6" data-component-path="client/src/pages/Shop.jsx" data-component-line="152:14" data-component-id="client/src/pages/Shop.jsx:152:14">
                <h3 className="font-medium text-gray-800 mb-3" data-component-path="client/src/pages/Shop.jsx" data-component-line="153:16" data-component-id="client/src/pages/Shop.jsx:153:16">Category</h3>
                <div className="space-y-2" data-component-path="client/src/pages/Shop.jsx" data-component-line="154:16" data-component-id="client/src/pages/Shop.jsx:154:16">
                  <button
                    onClick={() => setSelectedCategory('All')}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      selectedCategory === 'All'
                        ? 'bg-champagne-100 text-champagne-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    All
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                        selectedCategory === category.name
                          ? 'bg-champagne-100 text-champagne-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      data-component-path="client/src/pages/Shop.jsx" data-component-line="156:20" data-component-id="client/src/pages/Shop.jsx:156:20"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Slider Filter */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-800">Price Range</h3>
                  <button 
                    onClick={handleResetPrice}
                    className="text-xs text-champagne-600 hover:text-champagne-700"
                  >
                    Reset
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>₹{priceMin.toLocaleString()}</span>
                    <span>₹{priceMax.toLocaleString()}</span>
                  </div>
                  <div className="relative h-2">
                    {/* Track background */}
                    <div className="absolute w-full h-2 bg-gray-200 rounded-full"></div>
                    {/* Active track */}
                    <div 
                      className="absolute h-2 bg-champagne-500 rounded-full"
                      style={{
                        left: `${((priceMin - sliderMin) / (sliderMax - sliderMin)) * 100}%`,
                        right: `${100 - ((priceMax - sliderMin) / (sliderMax - sliderMin)) * 100}%`
                      }}
                    ></div>
                    {/* Min slider */}
                    <input
                      type="range"
                      min={sliderMin}
                      max={sliderMax}
                      value={priceMin}
                      onChange={(e) => {
                        const val = Math.min(Number(e.target.value), priceMax - 100)
                        setPriceMin(val)
                      }}
                      className="absolute w-full h-2 appearance-none bg-transparent pointer-events-auto cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-champagne-600 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                    />
                    {/* Max slider */}
                    <input
                      type="range"
                      min={sliderMin}
                      max={sliderMax}
                      value={priceMax}
                      onChange={(e) => {
                        const val = Math.max(Number(e.target.value), priceMin + 100)
                        setPriceMax(val)
                      }}
                      className="absolute w-full h-2 appearance-none bg-transparent pointer-events-auto cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-champagne-600 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                    />
                  </div>
                  {/* Input fields */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={priceMin}
                        onChange={(e) => setPriceMin(Math.max(sliderMin, Math.min(Number(e.target.value), priceMax - 100)))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-champagne-500 focus:border-champagne-500 outline-none"
                        placeholder="Min"
                      />
                    </div>
                    <span className="text-gray-400">-</span>
                    <div className="flex-1">
                      <input
                        type="number"
                        value={priceMax}
                        onChange={(e) => setPriceMax(Math.min(sliderMax, Math.max(Number(e.target.value), priceMin + 100)))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-champagne-500 focus:border-champagne-500 outline-none"
                        placeholder="Max"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1" data-component-path="client/src/pages/Shop.jsx" data-component-line="193:10" data-component-id="client/src/pages/Shop.jsx:193:10">
            {loading ? (
              <div className="text-center py-16">
                <p className="text-gray-600 text-lg">Loading products...</p>
              </div>
            ) : (
            <>
            <div className="mb-6 flex items-center justify-between" data-component-path="client/src/pages/Shop.jsx" data-component-line="194:12" data-component-id="client/src/pages/Shop.jsx:194:12">
              <p className="text-gray-600" data-component-path="client/src/pages/Shop.jsx" data-component-line="195:14" data-component-id="client/src/pages/Shop.jsx:195:14">
                Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
              </p>
            </div>

            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-component-path="client/src/pages/Shop.jsx" data-component-line="201:14" data-component-id="client/src/pages/Shop.jsx:201:14">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16" data-component-path="client/src/pages/Shop.jsx" data-component-line="207:14" data-component-id="client/src/pages/Shop.jsx:207:14">
                <p className="text-gray-600 text-lg" data-component-path="client/src/pages/Shop.jsx" data-component-line="208:16" data-component-id="client/src/pages/Shop.jsx:208:16">No products found matching your filters.</p>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Shop
